// League workspace — Umpiring Pool view.
//
// Thin shell that hosts three sub-tabs:
//   1. Umpires              — standing pool (add / edit / remove)
//   2. Upcoming Assignments — this-weekend matches + poll toggle +
//                             per-match nomination list
//   3. Umpiring Assignments — admin's picking board + printable flyer
//
// The third tab is gated to ADMIN / PRESIDENT / VICE_PRESIDENT /
// GENERAL_SECRETARY — the same tier that runs role management.

import React, { useEffect, useState } from "react";
import { Plus, Gavel, CalendarClock, ClipboardCheck, Upload, FileSpreadsheet } from "lucide-react";
import { useToast } from "../../components/Toast";
import { api } from "../../api";

import UmpiresListTab           from "./umpires/UmpiresListTab";
import UpcomingAssignmentsTab   from "./umpires/UpcomingAssignmentsTab";
import UmpiringAssignmentsTab   from "./umpires/UmpiringAssignmentsTab";

const ASSIGNMENT_ADMIN_ROLES = [
  "ADMIN", "PRESIDENT", "VICE_PRESIDENT", "GENERAL_SECRETARY"
];

const SUB_TABS = [
  { key: "list",       label: "Umpires",              Icon: Gavel },
  { key: "upcoming",   label: "Upcoming Assignments", Icon: CalendarClock },
  { key: "assignments",label: "Umpiring Assignments", Icon: ClipboardCheck, adminOnly: true }
];

export default function LeagueUmpires({ user }) {
  const toast = useToast();
  const [tab,      setTab]      = useState("list");
  const [umpires,  setUmpires]  = useState([]);
  const [loading,  setLoading]  = useState(true);

  async function refreshUmpires() {
    setLoading(true);
    try {
      const rows = await api.listUmpires();
      setUmpires(rows);
    } catch (err) {
      toast.error(err.message || "Couldn't load umpires.");
    } finally { setLoading(false); }
  }
  useEffect(() => { refreshUmpires(); /* eslint-disable-next-line */ }, []);

  const isAdmin = (user?.roles || []).some(r => ASSIGNMENT_ADMIN_ROLES.includes(r));
  const visibleTabs = SUB_TABS.filter(t => !t.adminOnly || isAdmin);

  function fireAdd() {
    // The Umpires-list tab listens for this custom event so the
    // page-level "Add New Umpire" button can trigger its modal
    // without prop-drilling refs down here.
    window.dispatchEvent(new CustomEvent("league-umpire-open-create"));
  }
  function fireUpload() {
    window.dispatchEvent(new CustomEvent("league-umpire-open-upload"));
  }
  async function downloadTemplate() {
    try { await api.downloadUmpireTemplate(); }
    catch (err) { toast.error(err.message || "Template download failed."); }
  }

  return (
    <div className="stack league-view">
      <div className="builderTop">
        <div>
          <p>Pittsburgh Cricket League</p>
          <h2>Umpiring Pool</h2>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {tab === "list" && (
            <>
              <button className="dark" onClick={downloadTemplate}>
                <FileSpreadsheet size={16}/> Template
              </button>
              <button className="dark" onClick={fireUpload}>
                <Upload size={16}/> Upload Excel
              </button>
              <button className="btn" onClick={fireAdd}>
                <Plus size={16}/> Add New Umpire
              </button>
            </>
          )}
        </div>
      </div>

      <div className="league-subTabs" role="tablist" aria-label="Umpiring Pool sections">
        {visibleTabs.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`league-subTab${tab === key ? " league-subTab--active" : ""}`}
            onClick={() => setTab(key)}
          >
            <Icon size={13}/> {label}
            {key === "list" && umpires.length > 0 && (
              <span className="league-subTab-count">{umpires.length}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "list" && (
        <UmpiresListTab
          umpires={umpires}
          loading={loading}
          refresh={refreshUmpires}
        />
      )}
      {tab === "upcoming" && (
        <UpcomingAssignmentsTab umpires={umpires} refresh={refreshUmpires}/>
      )}
      {tab === "assignments" && isAdmin && (
        <UmpiringAssignmentsTab/>
      )}
    </div>
  );
}
